// src/lib/analytics/consent.ts
/**
 * @fileoverview Analytics consent gate for the client-side event layer.
 * @description Decides whether the site's own analytics events may fire, with a
 * tiny in-memory cache so rapid event bursts don't re-read localStorage on every
 * call.
 *
 * Model (matches the region-aware Consent Mode v2 defaults in `./consent-mode`;
 * see docs/analytics/first-party-analytics-posture.md for the canonical policy):
 * - Rest of world: opt-OUT — analytics is allowed unless the visitor has stored
 *   an explicit opt-out. A Global Privacy Control signal is a sale/share opt-out
 *   (honored on the ad_* signals in `./consent-mode`); it does NOT deny
 *   first-party analytics here.
 * - EEA/UK/CH: Google's Consent Mode default (`denied`) handles Google-side
 *   gating at the tag layer before opt-in; this JS gate stays opt-out so the
 *   rest of the world behaves as before.
 *
 * Cache invalidation is **event-driven — there is no poller/`setInterval`**:
 * - cross-tab changes arrive via the `storage` event (fires only in OTHER
 *   tabs), which also re-emits `CONSENT_CHANGED_EVENT` locally so every
 *   subscriber sees cross-tab choices too;
 * - same-tab changes are signalled by the privacy UI dispatching
 *   `CONSENT_CHANGED_EVENT` right after it writes (and calling
 *   `invalidateConsentCache()` directly).
 * A short TTL remains only as a backstop for a write that forgets to signal; it
 * is a max-age on a value read on demand, not a timer that runs work.
 */

import type { ConsentPreferences } from "~/types/consent"
import {
  CONSENT_ANALYTICS_EXPLICIT_COOKIE,
  REGION_CONSENT_REQUIRED_COOKIE,
} from "./cookie-names"

/** localStorage key the privacy UI writes consent preferences to. */
export const CONSENT_STORAGE_KEY = "adsights_consent_v1"

/** Same-tab signal the privacy UI dispatches on `window` after writing consent. */
export const CONSENT_CHANGED_EVENT = "adsights:consent-changed"

/**
 * Backstop max-age for the cached decision (NOT a poll). Event-driven
 * invalidation handles the real cases; this only bounds staleness if a write
 * ever happens without signalling.
 */
const CACHE_TTL_MS = 5 * 60_000

interface ConsentCache {
  allowed: boolean
  at: number
}

let cache: ConsentCache | null = null
let listenersBound = false

/**
 * Reads an explicit analytics grant/deny from the edge-readable consent cookies
 * when localStorage is unavailable (private mode, quota, or cross-tab mirror).
 *
 * @returns {boolean | undefined} True for explicit grant, false for explicit
 *   deny, undefined when no cookie signal is present.
 */
export function readAnalyticsGrantFromCookies(): boolean | undefined {
  if (typeof document === "undefined") return undefined
  try {
    const header = document.cookie
    if (header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=1`)) return true
    if (header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=0`)) return false
    // Do not treat EEA bootstrap mirror=0 (default-deny seed) as an explicit opt-out.
  } catch {
    return undefined
  }
  return undefined
}

/** Reads the allow/deny decision straight from storage + GPC (no cache). */
function readAllowed(): boolean {
  const cookieGrant = readAnalyticsGrantFromCookies()
  // Explicit cookie opt-out wins over stale localStorage grants (storage-failure saves).
  if (cookieGrant === false) return false

  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw) {
      const prefs = JSON.parse(raw) as ConsentPreferences
      return prefs.analytics === true
    }
  } catch {
    // Corrupt/unavailable storage → fall through to cookie / default.
  }
  if (cookieGrant === true) return true
  // No stored preference: opt-out model → first-party analytics is allowed.
  // GPC is a "do not sell/share" signal (CCPA/CPRA family); it governs the
  // ad_*/personalization (sale-share) Consent Mode signals — handled in the
  // Consent Mode layer — NOT first-party measurement. So GPC alone does not
  // deny first-party analytics here.
  return true
}

/**
 * Replay the current stored consent into Google Consent Mode in THIS tab.
 *
 * Used by the cross-tab `storage` listener: clearing the app-level cache isn't
 * enough — without this, an already-open tab keeps its previous (e.g. granted)
 * Consent Mode state until reload, so Google auto/Enhanced-Measurement events
 * could still use storage even after the user opted out in another tab. Maps
 * stored prefs → a `gtag('consent','update', …)`, factoring live GPC for the
 * ad/sale-share signals, and conservatively denies non-essential storage when
 * the preference was removed or is corrupt. No-op if gtag isn't present.
 */
function replayConsentModeToGtag(): void {
  if (typeof window === "undefined") return
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag !== "function") return

  const gpc =
    typeof navigator !== "undefined" && navigator.globalPrivacyControl === true
  let analytics = false
  let ads = false
  let functional = true

  const cookieGrant = readAnalyticsGrantFromCookies()
  if (cookieGrant === false) {
    analytics = false
    ads = false
    functional = true
  } else {
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
      if (raw) {
        const prefs = JSON.parse(raw) as ConsentPreferences
        analytics = prefs.analytics === true
        ads = prefs.ads === true && prefs.doNotSellOrShare !== true && !gpc
        functional = prefs.functional === true
      } else if (cookieGrant === true) {
        analytics = true
      }
      // No stored pref (removed): fall through to the conservative denied default.
    } catch {
      analytics = false
      ads = false
      functional = true
    }
  }
  const g = (allowed: boolean): "granted" | "denied" => (allowed ? "granted" : "denied")
  gtag("consent", "update", {
    analytics_storage: g(analytics),
    ad_storage: g(ads),
    ad_user_data: g(ads),
    ad_personalization: g(ads),
    personalization_storage: g(ads),
    functionality_storage: g(functional),
  })
}

/**
 * Bind the (one-time, passive) invalidation listeners. Both are event-driven;
 * neither runs on a timer.
 */
function bindInvalidationListeners(): void {
  if (listenersBound || typeof window === "undefined") return
  listenersBound = true
  // Cross-tab: `storage` fires in OTHER tabs when localStorage changes. Drop the
  // cached decision, replay Consent Mode so this tab's Google tag reflects the
  // new choice without needing a reload, and RE-EMIT the app-level change
  // event so every same-tab subscriber (banner UI sync, engagement trackers'
  // opt-out purges, client-context cache, the RUM consent-epoch watcher —
  // r3547054576) reacts to a choice made in another tab exactly as it would
  // to one made here. Without the re-emit, a cross-tab withdrawal invalidated
  // the cache but left CONSENT_CHANGED_EVENT subscribers unnotified — e.g.
  // the RUM epoch never advanced, so a tab holding pre-withdrawal samples
  // could transmit them after a cross-tab off→on toggle. `storage` never
  // fires in the writing tab (which already dispatched via
  // `notifyConsentChanged`), so this cannot double-notify.
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key === null || e.key === CONSENT_STORAGE_KEY) {
      invalidateConsentCache()
      replayConsentModeToGtag()
      window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT))
    }
  })
  // Same-tab: dispatched by the privacy UI immediately after it writes (the UI
  // already pushed its own Consent Mode update, so just drop the cache here).
  window.addEventListener(CONSENT_CHANGED_EVENT, invalidateConsentCache)
}

/** Drop the cached decision so the next check re-reads consent. */
export function invalidateConsentCache(): void {
  cache = null
}

/**
 * Whether the site's analytics events may fire right now.
 * Always `true` on the server (the emit primitives no-op there regardless).
 */
export function isAnalyticsAllowed(): boolean {
  if (typeof window === "undefined") return true
  bindInvalidationListeners()
  const now = Date.now()
  if (cache && now - cache.at < CACHE_TTL_MS) return cache.allowed
  const allowed = readAllowed()
  cache = { allowed, at: now }
  return allowed
}

/**
 * Whether first-party analytics session storage may be written client-side.
 *
 * Mirrors the attribution layer: rest-of-world opt-out (`isAnalyticsAllowed`)
 * plus an explicit opt-in requirement in EEA/UK/CH before persisting journey
 * or engagement counters in sessionStorage.
 *
 * @returns {boolean} True when analytics storage writes are permitted.
 */
export function isAnalyticsStorageAllowed(): boolean {
  if (!isAnalyticsAllowed()) return false
  if (typeof document !== "undefined") {
    try {
      if (
        document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`) &&
        !hasExplicitAnalyticsOptIn()
      ) {
        return false
      }
    } catch {
      // Can't read the region cookie (rare private-mode throw): don't deny
      // first-party storage for everyone. The confirmed EEA case is gated
      // above; an unreadable cookie falls through to the opt-out default
      // (collect) rather than failing closed worldwide.
    }
  }
  return true
}


/**
 * Whether the visitor has stored an EXPLICIT analytics opt-IN (a saved
 * preference with `analytics === true`). Distinct from {@link isAnalyticsAllowed},
 * which returns `true` for the rest-of-world opt-out DEFAULT too: this returns
 * `true` ONLY for a deliberate grant. Used by the region-aware gates so an
 * EEA/UK/CH visitor (consent required) persists/transmits attribution only after
 * actually opting in — while the absence of any stored choice stays "no signal,
 * let the region decide" rather than a false grant.
 */
export function hasExplicitAnalyticsOptIn(): boolean {
  if (typeof window === "undefined") return false
  const cookieGrant = readAnalyticsGrantFromCookies()
  if (cookieGrant === false) return false
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw) {
      const prefs = JSON.parse(raw) as ConsentPreferences
      return prefs.analytics === true
    }
  } catch {
    // fall through to cookie mirror
  }
  return cookieGrant === true
}

/**
 * Whether the visitor has stored an EXPLICIT analytics opt-OUT (a saved
 * preference with `analytics === false`). The mirror of
 * {@link hasExplicitAnalyticsOptIn}: it is `true` ONLY for a deliberate opt-out,
 * NOT for the rest-of-world opt-out DEFAULT (no stored choice) and NOT for a GPC
 * signal. Used to purge/suppress first-party attribution in EVERY open tab after
 * an opt-out — `localStorage` is shared (so the cross-session record is already
 * gone), but each tab keeps its own `sessionStorage` nav pair, so a read in
 * another tab must honor the explicit withdrawal too.
 */
export function hasExplicitAnalyticsOptOut(): boolean {
  if (typeof window === "undefined") return false
  if (readAnalyticsGrantFromCookies() === false) return true
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return false
    const prefs = JSON.parse(raw) as ConsentPreferences
    return prefs.analytics === false
  } catch {
    return false
  }
}

/**
 * Whether analytics was effectively active before a save/replay decision.
 *
 * Differs from {@link hasExplicitAnalyticsOptIn}: rest-of-world visitors with no
 * stored choice count as effectively active (initial pageview already fired), while
 * EEA/UK/CH visitors without an explicit grant do not until opt-in.
 *
 * @returns {boolean} True when analytics was already effectively granted.
 */
export function wasAnalyticsEffectivelyAllowed(): boolean {
  if (typeof window === "undefined") return false

  const cookieGrant = readAnalyticsGrantFromCookies()
  if (cookieGrant === false) return false

  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw) {
      const prefs = JSON.parse(raw) as ConsentPreferences
      return prefs.analytics === true
    }
  } catch {
    // fall through to regional default
  }

  if (cookieGrant === true) return true

  // GPC governs sale/share (ad_*) signals, not first-party analytics — so it
  // does not flip this first-party "was analytics allowed" decision. The
  // EEA/UK/CH consent-for-storage requirement still applies below.
  if (typeof document !== "undefined") {
    try {
      if (document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`)) {
        return false
      }
    } catch {
      // ignore
    }
  }

  return true
}

/**
 * Tri-state analytics-consent signal to hand the server at a conversion (e.g.
 * the checkout POST), so the SERVER can apply the region-aware decision the
 * client can't make (the app never learns its region — by design):
 *
 * - `true`  — explicit stored opt-IN → granted everywhere.
 * - `false` — explicit stored opt-OUT → denied. (A GPC signal does NOT force
 *   `false`: GPC is a sale/share opt-out, not a first-party analytics opt-out,
 *   so it must not suppress our own server-side conversion measurement.)
 * - `undefined` — NO stored choice → "no signal"; the server decides by region
 *   (rest-of-world opt-out = granted; EEA/UK/CH default-deny = denied).
 *
 * The server (`createCheckoutSession`) reads `x-vercel-ip-country` and resolves
 * the `undefined` case, so EEA visitors aren't reported to GA4 server-side
 * without consent, while the rest-of-world opt-out majority is unaffected.
 */
export function analyticsConsentSignal(): boolean | undefined {
  if (typeof window === "undefined") return undefined
  const cookieGrant = readAnalyticsGrantFromCookies()
  if (cookieGrant === false) return false
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw) {
      const prefs = JSON.parse(raw) as ConsentPreferences
      return prefs.analytics === true
    }
  } catch {
    // Corrupt/unavailable storage → fall through to cookie mirror.
  }
  if (cookieGrant === true) return true
  // No stored choice → "no signal"; the server decides by region. GPC is a
  // sale/share opt-out, not a first-party analytics denial, so it does not
  // force a `false` here (which would suppress our own server-side conversion
  // measurement for rest-of-world GPC visitors).
  return undefined
}

/**
 * Whether the visitor has opted out of SALE/SHARING — a Global Privacy Control
 * signal, or an explicit stored ad / do-not-sell-share opt-out. Distinct from
 * {@link analyticsConsentSignal}: GPC is a sale/share opt-out, NOT a first-party
 * analytics opt-out, so a GPC visitor still gets first-party measurement while
 * the ad signals are denied. Handed to the server at a conversion so the
 * server-side Measurement-Protocol purchase can mark `ad_user_data` /
 * `ad_personalization` DENIED (see docs/analytics/first-party-analytics-posture.md).
 *
 * @returns {boolean} True when the ad/sale-share signals must be denied.
 */
export function adSaleShareDenied(): boolean {
  if (typeof window === "undefined") return false
  if (typeof navigator !== "undefined" && navigator.globalPrivacyControl === true) return true
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw) {
      const prefs = JSON.parse(raw) as ConsentPreferences
      if (prefs.doNotSellOrShare === true) return true
      if (prefs.ads === false) return true
    }
  } catch {
    // ignore — no readable stored opt-out
  }
  return false
}

/**
 * Notify the analytics layer (this tab) that consent changed. The privacy UI
 * calls this right after persisting, so an opt-out takes effect on the very
 * next event instead of waiting out the cache.
 */
export function notifyConsentChanged(): void {
  invalidateConsentCache()
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT))
  }
}
